import { abTranslate } from '@wix/ab-translate/browser';
import { User } from '@wix/ambassador-identity-account-v2-account/build/cjs/types.impl';
import { BroadridgeModal } from '@wix/cc-user-facing-common-components';
import { WixDesignSystemProvider } from '@wix/design-system';
import { createEssentials } from '@wix/fe-essentials-standalone';
import { RootEssentialsProvider } from '@wix/fe-essentials-standalone/react';
import type { ExperimentsBag } from '@wix/wix-experiments';
import { I18nextProvider, initI18n, Messages } from '@wix/wix-i18n-config';
import App, { AppContext, AppProps } from 'next/app';
import Script from 'next/script';
import React, { useEffect, useMemo } from 'react';

import { Error } from '../components/Error';
import { GlobalLoader } from '../components/GlobalLoader';
import Header from '../components/HeaderWrapper';
import { NotificationBar } from '../components/NotificationBar';
import { PageDataSync } from '../components/PageDataSync';
import { PageTemplate, PageData } from '../components/PageTemplate';
import { HEADER_FOOTER_SUPPORT_LANGUAGES, LOCALES } from '../constants';
import HistoryProvider from '../hooks/useRouteHistory';
import { PageType, PageSeo, SessionBIType } from '../types';
import {
  hasAnswersSSO,
  isPersonalPage,
  loginWithAnswersSSO,
} from '../utils/login';
import '../styles/globals.scss';
import '@wix/cc-user-facing-common-components/src/style.scss';
import '@wix/design-system/styles.global.css';
import 'normalize.css/normalize.css';
import StoreProvider from '../zustand/store-provider';
import { Store } from '../zustand/stores';

type PageProps = {
  pageData?: PageData;
  pageType: PageType;
  itemId?: string;
  itemType?: string;
  experiments: ExperimentsBag;
  pageSeo: PageSeo | null;
  hasError?: boolean;
  messageError?: string;
  statusCode?: number;
  initialStoreState: Store;
  user?: User;
};

type CustomAppProps = AppProps & {
  locale: string;
  messages: Messages;
  experiments: ExperimentsBag;
  pageProps: PageProps;
};

const CustomApp = ({
  Component,
  pageProps,
  locale,
  messages,
}: CustomAppProps) => {
  const {
    pageType,
    initialStoreState,
    experiments,
    pageSeo,
    hasError,
    messageError,
    statusCode,
    itemId,
    itemType,
    pageData,
    user,
  } = pageProps;

  const i18n = initI18n({
    bindI18n: 'languageChanged',
    locale: locale === LOCALES.ZH_HANT ? 'zh' : locale,
    messages: experiments ? abTranslate(experiments, messages) : messages,
  });

  useEffect(() => {
    const userIsLoggedInToWixOnly = user && !hasAnswersSSO();
    if (isLoginRequired(pageType, !!user) || userIsLoggedInToWixOnly) {
      loginWithAnswersSSO(location.href, locale);
    }
    if (!user) {
      localStorage.setItem('hc_session_bi_type', SessionBIType.VISITOR);
    }
  }, [user, locale, pageType]);

  const essentials = createEssentials({
    environment: {
      language: locale,
      artifactId: 'kb-client-nextjs',
      version: '0.1.0',
    },
    experiments: { bag: pageProps.experiments },
    fedops: {},
    httpClient: {
      baseURL: `/${locale}/`,
    },
  });

  const thirdPartyScripts = useMemo(() => {
    if (globalThis?.location && location.hostname === 'localhost') {
      return null;
    }

    const integrationLocale = HEADER_FOOTER_SUPPORT_LANGUAGES.includes(
      locale as string,
    )
      ? locale
      : LOCALES.EN;

    return (
      <>
        <Script src="https://apps.wix.com/answers-wix-integration/cookie-policy/consent-manager.js" />
        <Script src="https://apps.wix.com/answers-wix-integration/cookie-policy/tag-manager.js" />
        <Script
          src={`https://apps.wix.com/answers-wix-integration/cookie-policy/${integrationLocale}/setup.js`}
          strategy="lazyOnload"
        />
        <Script
          src="https://static.parastorage.com/unpkg-semver/wix-recorder@^1/app.bundle.min.js"
          strategy="lazyOnload"
        />
      </>
    );
  }, [locale, globalThis?.location?.hostname]);

  if (initialStoreState?.userSlice?.isBroadridgeUser) {
    return (
      <div>
        <BroadridgeModal isOpen={true} locale={locale || 'en'} />
      </div>
    );
  }

  return (
    <StoreProvider {...initialStoreState}>
      <PageDataSync
        pageData={pageData}
        itemId={itemId}
        itemType={itemType}
        hasError={hasError}
      >
        <HistoryProvider>
          <RootEssentialsProvider essentials={essentials}>
            <I18nextProvider i18n={i18n}>
              <WixDesignSystemProvider features={{ newColorsBranding: true }}>
                <Header />
                <NotificationBar pageType={pageType} />
                <PageTemplate pageType={pageType} pageSeo={pageSeo}>
                  <GlobalLoader />
                  {hasError && (
                    <Error
                      statusCode={statusCode as number}
                      messageError={messageError}
                    />
                  )}
                  {!hasError && isLoginRequired(pageType, !!user) && <></>}
                  {!hasError && !isLoginRequired(pageType, !!user) && (
                    <Component {...pageProps} />
                  )}
                </PageTemplate>
                {thirdPartyScripts}
              </WixDesignSystemProvider>
            </I18nextProvider>
          </RootEssentialsProvider>
        </HistoryProvider>
      </PageDataSync>
    </StoreProvider>
  );
};

CustomApp.getInitialProps = async (context: AppContext) => {
  let { locale } = context.router;
  if (!Object.values(LOCALES).includes(locale as string)) {
    locale = LOCALES.EN;
  }
  const { pageProps } = await App.getInitialProps(context);
  const messages = await import(
    `../locales/messages_${locale === LOCALES.ZH_HANT ? 'zh' : locale}.json`
  );
  return { pageProps, locale, messages };
};

const isLoginRequired = (pageType: PageType, isLoggedInUser?: boolean) =>
  isPersonalPage(pageType) && (!isLoggedInUser || !hasAnswersSSO());

export default CustomApp;
